import { useEffect, useState } from "react";
import { Copy, Gift, TrendingUp, Users, DollarSign } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";

export default function Referrals() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/referrals/me").then((r) => setData(r.data));
  }, []);

  if (!data) return <div className="text-slate-500">Loading…</div>;

  const link = `${window.location.origin}/register?ref=${data.referral_code}`;

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <div className="space-y-6">
      <header>
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Refer & Earn</div>
        <h1 className="font-heading text-4xl tracking-tight" data-testid="referral-hero-heading">
          Earn {(data.totals.commission_rate * 100).toFixed(0)}% Recurring
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          Share your link. Earn {(data.totals.commission_rate * 100).toFixed(0)}% of every payment from people you refer, forever, for as long as they stay subscribed.
        </p>
      </header>

      {/* Hero card */}
      <div className="ae-card p-8 relative overflow-hidden">
        <div className="absolute -top-12 -right-12 h-44 w-44 rounded-full bg-emerald-500/15 blur-3xl" />
        <div className="relative grid md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Gift className="h-4 w-4 text-emerald-400" />
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Your unique code</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="font-heading text-5xl font-bold tracking-tight font-mono text-emerald-400 tabular-nums" data-testid="referral-code">
                {data.referral_code}
              </div>
              <button onClick={() => copy(data.referral_code, "Code")} data-testid="copy-code-btn" className="btn-ghost text-xs inline-flex items-center gap-1.5">
                <Copy className="h-3 w-3" /> Copy
              </button>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Your share link</div>
            <div className="flex gap-2">
              <code className="flex-1 bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2 text-xs font-mono break-all" data-testid="referral-link">{link}</code>
              <button onClick={() => copy(link, "Link")} data-testid="copy-link-btn" className="btn-primary text-xs inline-flex items-center gap-1.5">
                <Copy className="h-3 w-3" /> Copy link
              </button>
            </div>
            <p className="text-[10px] text-slate-500 mt-2">
              When someone signs up with this link and upgrades, you earn {(data.totals.commission_rate * 100).toFixed(0)}% commission on every renewal payment.
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Users} label="People referred" value={data.referred_count} testid="stat-referred" />
        <StatCard icon={DollarSign} label="Lifetime earned" value={`$${data.totals.lifetime_amount.toFixed(2)}`} testid="stat-lifetime" accent />
        <StatCard icon={TrendingUp} label="Pending payout" value={`$${data.totals.pending_amount.toFixed(2)}`} testid="stat-pending" />
        <StatCard icon={Gift} label="Paid out" value={`$${data.totals.paid_amount.toFixed(2)}`} testid="stat-paid" />
      </div>

      {/* Referred users */}
      <div className="ae-card p-6">
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-4">People you've referred ({data.referred_count})</div>
        {data.referred_users.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">
            No referrals yet. Share your link to start earning.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {data.referred_users.map((u) => (
              <div key={u.uid} data-testid={`referred-${u.uid}`} className="py-3 flex items-center gap-3">
                <div className="h-8 w-8 rounded-sm bg-white/5 flex items-center justify-center font-mono text-xs">
                  {u.name?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm">{u.name}</div>
                  <div className="text-[11px] text-slate-500">{u.email}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border font-mono ${u.tier === "free" ? "bg-slate-500/10 text-slate-400 border-slate-500/30" : "bg-emerald-500/15 text-emerald-300 border-emerald-500/40"}`}>
                  {u.tier}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Commissions */}
      <div className="ae-card p-6">
        <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-4">Commissions ({data.commissions.length})</div>
        {data.commissions.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">
            No commissions yet. They appear when your referrals upgrade or renew.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-white/8">
                  <th className="text-left py-2">Date</th>
                  <th className="text-left py-2">Referred</th>
                  <th className="text-right py-2">Payment</th>
                  <th className="text-right py-2">Your cut</th>
                  <th className="text-right py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.commissions.map((c) => (
                  <tr key={c.rid} className="border-b border-white/5">
                    <td className="py-2 text-xs font-mono text-slate-400">{fmtDateTime(c.created_at)}</td>
                    <td className="py-2 text-xs text-slate-300">{c.referred_email}</td>
                    <td className="py-2 text-right font-mono tabular-nums">${c.payment_amount.toFixed(2)}</td>
                    <td className="py-2 text-right font-mono tabular-nums text-emerald-400">${c.commission_amount.toFixed(2)}</td>
                    <td className="py-2 text-right">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${c.status === "paid" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : "bg-amber-500/15 text-amber-300 border-amber-500/40"}`}>
                        {c.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: I, label, value, testid, accent }) {
  return (
    <div className={`ae-card p-5 ${accent ? "border-emerald-500/30" : ""}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
        <I className={`h-4 w-4 ${accent ? "text-emerald-400" : "text-slate-500"}`} />
      </div>
      <div className={`font-mono tabular-nums text-3xl font-bold mt-2 ${accent ? "text-emerald-400" : "text-slate-100"}`}>{value}</div>
    </div>
  );
}
